import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type {
  EstimateDamageLocation,
  EstimateRequestDamageInformationFormValues,
} from "features/EstimateRequest/entities";
import { useEstimateRequestEnumTranslations } from "features/EstimateRequest/hooks/useEstimateRequestEnumTranslations";
import Image from "next/image";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CarCloseupImage from "./car_closeup.png";
import CarWideImage from "./car_wide.png";
import { EstimateRequestImageUpload } from "./EstimateRequestImageUpload";

export function DamageLocationImageUploadCard({
  location,
  formMethods,
  index,
  shopId,
  estimateRequestId,
}: {
  location: EstimateDamageLocation;
  formMethods: UseFormReturn<EstimateRequestDamageInformationFormValues>;
  index: number;
  shopId: string;
  estimateRequestId: string;
}) {
  const enumTranslations = useEstimateRequestEnumTranslations();
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:DamageLocationImageUploadCard",
  });

  const basePath = `damageLocations.${index}.images` as const;

  return (
    <Card
      variant="outlined"
      sx={{
        backgroundColor: "background.default",
        minWidth: "min(100%, 325px)",
        flex: { xs: 1, md: "initial" },
      }}
      data-testid="damage-card"
    >
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {enumTranslations.EstimateImageLocation[location]}
        </Typography>
        <Stack
          direction="row"
          gap={2}
          justifyContent="space-around"
          flexWrap="wrap"
        >
          <EstimateRequestImageUpload
            control={formMethods.control}
            estimateRequestId={estimateRequestId}
            shopId={shopId}
            fileFieldName={`${basePath}.0.file`}
            fileNameFieldName={`${basePath}.0.fileName`}
            fileIdFieldName={`${basePath}.0.fileId`}
            location={location}
            imageNumber={1}
            imagePlaceholderSlot={
              <Tooltip title={t("imageDescriptionCloseup")} placement="top">
                <Image
                  src={CarCloseupImage}
                  alt={t("imageDescriptionCloseup")}
                  style={{
                    objectFit: "contain",
                    opacity: 0.3,
                    gridArea: "stack",
                  }}
                  width={175}
                  height={100}
                />
              </Tooltip>
            }
          />
          <EstimateRequestImageUpload
            control={formMethods.control}
            estimateRequestId={estimateRequestId}
            shopId={shopId}
            fileFieldName={`${basePath}.1.file`}
            fileNameFieldName={`${basePath}.1.fileName`}
            fileIdFieldName={`${basePath}.1.fileId`}
            location={location}
            imageNumber={2}
            imagePlaceholderSlot={
              <Tooltip title={t("imageDescriptionWide")} placement="top">
                <Image
                  src={CarWideImage}
                  alt={t("imageDescriptionWide")}
                  style={{ objectFit: "contain", opacity: 0.3 }}
                  width={175}
                  height={100}
                />
              </Tooltip>
            }
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
